// Модель «Избранного» (макет 1073:19438, HQ-1057) без React: матчи по дням, подписи плиток из полей полки сервера.
// Поля нет — подписи нет. «Next match TBA» — только у команды в таблице без ближайшего матча (HQ-1273).
import type { LeagueCard, PlayerCard } from '../../api/favoritesTypes';
import type { TeamCard } from '../../api/homeTypes';
import type { MatchRow, TeamRef } from '../../api/types';
import { formatDate, t } from '../../i18n/i18n';
import { fixed } from '../../ui/format';

/** Ключ дня в часовом поясе устройства: «2026-10-20». */
export function localDayKey(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Сколько суток от сегодня до дня матча (по местному календарю, не по 24 часам). */
function dayOffset(date: Date, now: Date): number {
  const a = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a - b) / 86_400_000);
}

/** Заголовок дня по макету: «Yesterday, 19 Oct» / «Today, 20 Oct» / «Tomorrow, 21 Oct» / «Sat, 17 Oct». */
export function dayTitle(date: Date, now: Date = new Date()): string {
  const near = { [-1]: t('mc_yesterday'), 0: t('mc_today'), 1: t('mc_tomorrow') }[dayOffset(date, now)];
  if (near) return t('tg_fav_day', near, formatDate(date, { day: 'numeric', month: 'short' }));
  return formatDate(date, { weekday: 'short', day: 'numeric', month: 'short' });
}

export interface MatchDayGroup { key: string; title: string; matches: MatchRow[] }

/** Предстоящие и прошлые матчи полки — одной лентой по времени начала, группами по местному дню. Дубли по id — один раз. */
export function groupByDay(matches: MatchRow[], now: Date = new Date()): MatchDayGroup[] {
  const unique = [...new Map(matches.map((m) => [m.id, m])).values()]
    .sort((a, b) => Date.parse(a.starting_at) - Date.parse(b.starting_at));
  const groups: MatchDayGroup[] = [];
  for (const match of unique) {
    const start = new Date(match.starting_at);
    const key = localDayKey(start);
    const last = groups[groups.length - 1];
    if (last?.key === key) last.matches.push(match);
    else groups.push({ key, title: dayTitle(start, now), matches: [match] });
  }
  return groups;
}

/** Соперник команды в матче — та, что не она; команда не в матче (или не задана) — соперника нет. */
export function opponent(match: MatchRow, teamId?: number): TeamRef | undefined {
  const { home, away } = match.teams ?? {};
  if (teamId != null && home?.id === teamId) return away;
  if (teamId != null && away?.id === teamId) return home;
  return undefined;
}

/** Ближайший матч плитки: «vs England» и «22 Oct at 08:00». Соперника не знаем — первая строка «India vs England». */
export function nextMatchLines(match: MatchRow, teamId?: number): { title: string; when: string } {
  const start = new Date(match.starting_at);
  const rival = opponent(match, teamId);
  const { home, away } = match.teams ?? {};
  const title = rival ? t('tg_fav_vs', rival.name) : home && away ? `${home.name} ${t('tg_fav_vs', away.name)}` : match.status_label;
  const when = t('tg_fav_at', formatDate(start, { day: 'numeric', month: 'short' }),
    formatDate(start, { hour: '2-digit', minute: '2-digit', hour12: false }));
  return { title, when };
}

/** Место команды «#2» — только из rank сервера. */
export function rankLabel(card: TeamCard): string | null {
  return card.rank ? `#${card.rank.position}` : null;
}

/** Где команда в таблице: «IPL 2026» — код турнира (нет кода — имя) и сезон; сезон уже начинается с турнира — без повтора. */
function rankWhere(rank: NonNullable<TeamCard['rank']>): string | undefined {
  const league = rank.league?.code || rank.league?.name;
  const season = rank.season?.name;
  if (!season || !league) return season ?? league ?? undefined;
  const names = [rank.league?.code, rank.league?.name].filter((n): n is string => !!n);
  return names.some((n) => season.startsWith(n)) ? season : `${league} ${season}`;
}

/** Без ближайшего матча у команды в таблице: «IPL 2026 · 8 pts» (макет 1073:20355; очков нет — без них). */
export function rankLine(card: TeamCard): string | null {
  if (!card.rank) return null;
  const points = card.rank.points != null ? t('tg_fav_pts', card.rank.points) : null;
  const parts = [rankWhere(card.rank), points].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/** Флажок турнира перед «#N» — только у клуба (у сборной место в рейтинге без флажка), если сервер прислал картинку. */
export function rankFlag(card: TeamCard): string | null {
  return card.rank && card.national_team !== true ? card.rank.league?.image ?? null : null;
}

/** Запись команды в топ-3 турнира «9-5,» перед очками; нет won/lost (старый ответ) — null. Ничьи без результата не пишем. */
export function leagueRecord(row: NonNullable<LeagueCard['standings_top']>[number]): string | null {
  return row.won != null && row.lost != null ? `${row.won}-${row.lost},` : null;
}

/** Топ-3 таблицы турнира: место, команда, очки. */
export function leagueTop(card: LeagueCard): NonNullable<LeagueCard['standings_top']> {
  return (card.standings_top ?? []).slice(0, 3);
}

/** Ключевое число карьеры игрока — средний бэттинга (35.9); null — числа нет. */
export function playerKeyNumber(card: PlayerCard): string | null {
  const avg = card.career?.batting_average;
  return avg == null ? null : fixed(avg, 1);
}

/** «Wicketkeeper • 35 y.o.» — что пришло. */
export function playerSubtitle(card: PlayerCard): string | null {
  const parts = [card.position, card.age != null ? t('mc_years_old', card.age) : null].filter(Boolean);
  return parts.length ? parts.join(' • ') : null;
}

/** Имя по макету: имя обычным, фамилия жирным с новой строки. Одно слово — только жирная строка. */
export function splitName(name: string): { first: string | null; last: string } {
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return { first: null, last: name };
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1]! };
}
